// @flow

import React from 'react';
import type { Node } from 'react';
import styled from 'styled-components';

import { useImages } from 'hooks';
import { Color, flex, h20, hoverChild, topPadding, Width } from 'styles';
import type { AllImages } from 'hooks/use-images';
import { ImageLoader } from 'components/Gui';

const Wrapper = styled.section`
  ${flex()};
  flex-direction: column;
  ${topPadding(48, 48, 84)};

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    align-items: flex-start;
  }

  > div {
    margin-top: 32px;
    @media print, screen and (min-width: ${Width.BREAK_XL}px) {
      display: flex;
    }
    > div {
      width: 100%;
      ${flex()};
      flex-direction: column;

      @media print, screen and (min-width: ${Width.BREAK_MD}px) {
        flex-direction: row;
        justify-content: space-between;
        align-items: stretch;
        max-width: 730px;
      }

      @media print, screen and (min-width: ${Width.BREAK_XL}px) {
        flex-direction: column;
        justify-content: flex-start;
        height: 35.6rem;
      }

      > div {
        ${flex()};
        flex-direction: column;
        text-align: center;
        margin-bottom: 32px;
        max-width: 340px;

        @media print, screen and (min-width: ${Width.BREAK_MD}px) {
          max-width: unset;
          width: 48%;
          flex: 0 0 auto;
          margin-bottom: 54px;
        }

        @media print, screen and (min-width: ${Width.BREAK_XL}px) {
          width: 100%;
          height: 53%;
          margin-bottom: 0;
          * {
            text-align: left;
          }
        }

        h4 {
          ${h20};
          margin-bottom: 14px;
        }

        p {
          margin-bottom: 14px;
        }

        a {
          ${hoverChild('h4', 'color', Color.BLACK_SOUL, Color.PURPLE_MAIN)};
        }
      }

      &:first-of-type {
        .image {
          flex: 0 0 auto;
          width: 140px;
          margin-bottom: 24px;

          @media print, screen and (min-width: ${Width.BREAK_XL}px) {
            width: 132px;
            margin-bottom: 0;
            margin-right: 32px;
          }
        }

        @media print, screen and (min-width: ${Width.BREAK_XL}px) {
          width: 678px;
          margin-right: 134px;
        }

        > div {
          @media print, screen and (min-width: ${Width.BREAK_XL}px) {
            flex-direction: row;
            align-items: flex-start;
            justify-content: flex-start;
          }
        }
      }

      &:last-of-type {
        @media print, screen and (min-width: ${Width.BREAK_XL}px) {
          width: 365px;
          > div {
            align-items: flex-start;
            justify-content: flex-start;
          }
        }
      }
    }
  }
`;

const Links = styled.div`
  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    margin-top: auto;
  }
  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    margin-top: unset;
  }
`;

const Text = styled.div`
  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    flex: 1 0 auto;
    ${flex()};
    flex-direction: column;
  }
  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    flex: unset;
    align-items: flex-start;
  }
`;

type Props = {
  title: Node | string,
  items: {
    image?: {
      name: string,
      alt: string,
    },
    text: Node,
    links: Node,
  }[],
  images: AllImages,
};

const OptionalImageBlurbList = ({ title, items, images, ...rest }: Props) => {
  const getImage = useImages(images);
  const withImages = items.filter(({ image }) => !!image);
  const withoutImages = items.filter(({ image }) => !image);
  return (
    <Wrapper {...rest}>
      {title}
      <div role="list">
        <div>
          {withImages.map(({ image: { name, alt }, text, links }, index) => (
            <div key={`OptionalImageBlurbListWithImages-${index}`} role="listitem">
              <div className="image">
                <ImageLoader image={getImage(name)} alt={alt} />
              </div>
              <Text>
                {text}
                <Links>{links}</Links>
              </Text>
            </div>
          ))}
        </div>
        <div>
          {withoutImages.map(({ text, links }, index) => (
            <Text key={`OptionalImageBlurbListWithoutImages-${index}`} role="listitem">
              {text}
              <Links>{links}</Links>
            </Text>
          ))}
        </div>
      </div>
    </Wrapper>
  );
};

export default OptionalImageBlurbList;
